import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { ScanLine, Brain, Languages, Mail, ArrowRight, ArrowDown, CheckCircle2, GraduationCap, FilePenLine, Sparkles, Target } from "lucide-react";

const evaluationSteps = [
  { icon: FilePenLine, title: "Write your answers", description: "Choose a plan and question paper. Practise on paper, just like your exam." },
  { icon: ScanLine, title: "Scan & upload", description: "Take clear photos of your handwritten answer sheets or upload a PDF." },
  { icon: Brain, title: "Get AI evaluation", description: "Your answers are checked against the marking scheme, with marks and explanations." },
  { icon: Target, title: "Know what to improve", description: "Review question-wise feedback and use your report to guide your next revision." },
];

const defaultPricingPlans = [
  {
    id: "focus",
    name: "Focus",
    plan_type_label: "1 subject \u00B7 1 set",
    price_inr: 149,
    subjects: ["Mathematics"],
    sets: 1,
    scripts_quota: 1,
    features: ["1 subject", "1 question paper set", "AI evaluation", "PDF report"],
  },
  {
    id: "practice",
    name: "Practice",
    plan_type_label: "1 subject \u00B7 multiple sets",
    price_inr: 499,
    subjects: ["Mathematics"],
    sets: 5,
    scripts_quota: 5,
    features: ["1 subject", "Up to 5 sets", "Detailed guidance", "PDF report"],
  },
  {
    id: "explorer",
    name: "Explorer",
    plan_type_label: "Multiple subjects \u00B7 1 set",
    price_inr: 399,
    subjects: ["Mathematics", "Science", "English"],
    sets: 1,
    scripts_quota: 3,
    features: ["3 subjects", "1 set each", "AI evaluation", "PDF report"],
  },
  {
    id: "achiever",
    name: "Achiever",
    plan_type_label: "Multiple subjects \u00B7 multiple sets",
    price_inr: 1499,
    subjects: ["Mathematics", "Science", "English", "History"],
    sets: 5,
    scripts_quota: 20,
    features: ["4 subjects", "Up to 5 sets each", "Priority evaluation", "All languages", "PDF report"],
  },
];

const normalizeDisplayText = (value = "") => String(value)
  .replace(/\u00C2\u00B7/g, "\u00B7")
  .replace(/\u00E2\u201A\u00B9/g, "\u20B9");

const getPlanCtaName = (name = "") => normalizeDisplayText(name).split("(", 1)[0].trim() || "plan";

function EvaluationPreview() {
  return (
    <div className="relative rounded-3xl border border-primary/15 bg-gradient-to-br from-primary/10 via-background to-secondary p-5 sm:p-8">
      <div className="flex items-center justify-between gap-3 mb-6">
        <span className="flex items-center gap-2 text-sm font-bold text-primary"><Sparkles aria-hidden="true" className="h-4 w-4" /> Your practice, explained</span>
        {/* <span className="rounded-full bg-background px-3 py-1 text-xs text-muted-foreground border border-border">Illustrative preview</span> */}
      </div>
      <div className="rounded-2xl border border-border bg-background p-5 sm:p-6 shadow-lg sm:-rotate-2">
        <div className="flex items-center gap-3 border-b border-border pb-4">
          <div className="rounded-xl bg-secondary p-3"><FilePenLine aria-hidden="true" className="h-6 w-6 text-primary" /></div>
          <div><p className="font-heading font-bold text-lg">Your handwritten answers</p><p className="text-xs text-muted-foreground">Write on paper. Upload from your phone.</p></div>
        </div>
        <div aria-hidden="true" className="space-y-3 py-5">
          <div className="h-2 w-4/5 rounded-full bg-muted-foreground/20" />
          <div className="h-2 w-full rounded-full bg-muted-foreground/15" />
          <div className="h-2 w-3/5 rounded-full bg-primary/25" />
        </div>
        <div className="flex items-center gap-2 text-xs font-medium text-primary"><CheckCircle2 aria-hidden="true" className="h-4 w-4" /> Ready for evaluation</div>
      </div>
      <div className="relative flex items-center justify-center gap-2 py-5 text-primary text-sm font-bold">
        <ArrowDown aria-hidden="true" className="h-5 w-5" /><Brain aria-hidden="true" className="h-5 w-5" /> AI checks each answer
      </div>
      <div className="relative rounded-2xl border border-primary/20 bg-background p-5 sm:p-6 shadow-lg sm:ml-8">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-heading font-bold text-lg">Understand every mark</h2>
          <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-bold text-primary">Sample</span>
        </div>
        <div className="flex items-center justify-between gap-3 mt-4 text-sm"><span className="text-muted-foreground">Question 1</span><span className="font-bold">3 / 5 marks</span></div>
        <div aria-hidden="true" className="flex gap-1.5 mt-2">{[0, 1, 2, 3, 4].map((mark) => <span key={mark} className={`h-2 flex-1 rounded-full ${mark < 3 ? "bg-primary" : "bg-secondary"}`} />)}</div>
        <p className="flex items-start gap-2 mt-4 text-sm"><CheckCircle2 aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> Clear explanation of the main concept.</p>
        <div className="rounded-xl bg-primary/5 p-3 mt-3"><p className="text-xs font-bold text-primary">YOUR NEXT STEP</p><p className="text-sm mt-1 text-muted-foreground">Include the missing steps and label your diagram to make your answer complete.</p></div>
      </div>
      <p className="flex items-center justify-center gap-2 mt-5 text-xs text-muted-foreground"><Mail aria-hidden="true" className="h-4 w-4" /> Evaluation report, also delivered by email</p>
    </div>
  );
}

const Nav = () => (
  <header className="sticky top-0 z-30 bg-white/70 dark:bg-background/70 backdrop-blur-xl border-b border-border">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-wrap gap-3 items-center justify-between">
      <span className="font-heading font-black text-xl sm:text-2xl tracking-tight text-primary">
        Theta<span className="text-foreground">Exams</span>
      </span>
      <div className="flex items-center gap-3">
        {/* <a href="#how-evaluation-works" className="hidden md:inline-flex text-sm font-medium text-muted-foreground hover:text-primary mr-3">How evaluation works</a> */}
        {/* <a href="#pricing" className="hidden md:inline-flex text-sm font-medium text-muted-foreground hover:text-primary mr-3">Pricing</a> */}
        <Link to="/login"><Button variant="ghost" data-testid="nav-login">Sign in</Button></Link>
        <Link to="/register"><Button className="rounded-full px-6" data-testid="nav-get-started">Get started</Button></Link>
      </div>
    </div>
  </header>
);

export default function Landing() {
  const [plans, setPlans] = useState(defaultPricingPlans);
  useEffect(() => {
    api.get("/plans").then((response) => {
      if (Array.isArray(response.data) && response.data.length > 0) {
        setPlans(response.data);
      }
    }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background relative">
      <Nav />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-10 lg:gap-12 pt-16 pb-24">
        <div className="lg:col-span-6 flex flex-col justify-center">
          <p className="overline text-primary mb-4">For SSLC & PUC students</p>
          <div className="flex flex-wrap gap-2 mb-5">
            <span className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-4 py-2 text-sm font-bold text-primary"><GraduationCap aria-hidden="true" className="h-4 w-4" /> SSLC</span>
            <span className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-4 py-2 text-sm font-bold text-primary"><GraduationCap aria-hidden="true" className="h-4 w-4" /> PUC</span>
          </div>
          <h1 className="font-heading font-black text-5xl sm:text-6xl tracking-tight leading-none">
            Your answers.<br />AI evaluation.<br /><span className="text-primary">A clearer way to improve.</span>
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed mt-6 max-w-xl">
            Preparing for SSLC or PUC exams? Turn your handwritten practice answers into question-wise marks and helpful feedback. See where you lost marks and what to revise next.
          </p>
          <div className="flex flex-wrap gap-4 mt-8">
            <Link to="/register">
              <Button size="lg" className="rounded-full px-8 gap-2" data-testid="hero-cta">
                Get started <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
            <Button asChild size="lg" variant="outline" className="rounded-full px-8">
              <a href="#how-evaluation-works">See how it works <ArrowDown aria-hidden="true" className="h-4 w-4" /></a>
            </Button>
          </div>
          <div className="flex flex-wrap gap-6 mt-10 text-sm text-muted-foreground">
            <span className="flex items-center gap-2"><Languages className="h-4 w-4 text-primary" /> 12+ languages</span>
            <span className="flex items-center gap-2"><Brain className="h-4 w-4 text-primary" /> Question-wise feedback</span>
            <span className="flex items-center gap-2"><Mail className="h-4 w-4 text-primary" /> Email reports</span>
          </div>
        </div>
        <div className="lg:col-span-6 flex items-center">
          <EvaluationPreview />
        </div>
      </section>

      {/* Evaluation journey */}
      <section id="how-evaluation-works" aria-labelledby="evaluation-heading" className="scroll-mt-28 bg-secondary/50 border-y border-border">
        <div className="max-w-7xl mx-auto px-6 py-16 sm:py-20">
          <div className="max-w-2xl">
            <p className="overline text-primary">From practice to progress</p>
            <h2 id="evaluation-heading" className="font-heading font-black text-3xl sm:text-4xl tracking-tight mt-3">SSLC and PUC. Make every practice paper count.</h2>
            <p className="text-muted-foreground mt-4 leading-relaxed">Go beyond a score. Understand your answers, spot gaps and approach your next practice paper with a clear revision plan.</p>
          </div>
          <ol className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-10">
            {evaluationSteps.map((step, index) => (
              <li key={step.title} className="relative bg-background border border-border rounded-2xl p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><step.icon aria-hidden="true" className="h-6 w-6" /></div>
                  <span className="font-heading font-bold text-3xl text-primary/30" aria-hidden="true">0{index + 1}</span>
                </div>
                <h3 className="font-heading font-bold text-lg"><span className="sr-only">Step {index + 1}: </span>{step.title}</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{step.description}</p>
                {index < evaluationSteps.length - 1 && <span aria-hidden="true" className="hidden lg:flex absolute top-10 -right-5 z-10 h-9 w-9 items-center justify-center rounded-full border border-border bg-background text-primary"><ArrowRight className="h-4 w-4" /></span>}
              </li>
            ))}
          </ol>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-5 mt-8 rounded-2xl bg-primary/5 border border-primary/15 p-6">
            <div><p className="font-heading font-bold text-lg">Ready to understand your answers better?</p><p className="text-sm text-muted-foreground mt-1">Create an account and choose an evaluation plan to begin.</p></div>
            <Button asChild className="rounded-full px-6 shrink-0"><Link to="/register">Start your evaluation <ArrowRight aria-hidden="true" className="h-4 w-4" /></Link></Button>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" aria-labelledby="pricing-heading" className="scroll-mt-28 max-w-7xl mx-auto px-6 py-20">
        <p className="overline text-primary">Pricing</p>
        <h2 id="pricing-heading" className="font-heading font-black text-4xl tracking-tight mt-2">Choose your evaluation plan</h2>
        <p className="text-muted-foreground mt-3 max-w-2xl">Start with one paper or practise across subjects and sets. Every plan includes AI evaluation and a detailed report.</p>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-10">
          {plans.map((p) => (
            <div key={p.id} data-testid={`plan-card-${p.name}`} className="bg-background border-2 border-primary/15 rounded-2xl p-6 flex flex-col shadow-sm hover:-translate-y-1 hover:border-primary/40 hover:shadow-lg transition-[transform,box-shadow,border-color] duration-200">
              <p className="overline text-primary">{normalizeDisplayText(p.plan_type_label)}</p>
              <h3 className="font-heading font-bold text-2xl mt-1">{normalizeDisplayText(p.name)}</h3>
              <p className="font-heading font-black text-4xl mt-4">{"\u20B9"}{p.price_inr}</p>
              <p className="text-sm text-muted-foreground">{p.sets} set(s) {"\u00B7"} {p.scripts_quota} evaluations</p>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {(p.subjects || []).map((s) => (
                  <span key={s} className="text-xs bg-secondary rounded-full px-2 py-0.5">{s}</span>
                ))}
              </div>
              <ul className="mt-5 space-y-2 flex-1">
                {(p.features || []).map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <Link to="/register" className="mt-6">
                <Button className="w-full rounded-full">Choose {getPlanCtaName(p.name)}</Button>
              </Link>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-border px-6 py-10 text-center text-sm text-muted-foreground">
        {/* <p>ThetaExams — Handwritten answer script evaluation.</p> */}
        <p className="mt-2">© {new Date().getFullYear()} ThetaExams. All rights reserved.</p>
      </footer>
    </div>
  );
}
